import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import notify from './utils/notify';
import './CourseManager.css';
import './monochrome.css';
import countries from './countries';

import './Home.css';
import { FiBarChart2, FiSettings, FiGrid, FiEdit, FiTrash2, FiSearch, FiPlus, FiChevronDown, FiChevronLeft, FiChevronRight, FiGlobe, FiBookmark, FiZap, FiCheck, FiX, FiMapPin, FiImage, FiCpu, FiAlertCircle, FiSkipForward, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { GiGolfTee } from 'react-icons/gi';
import { LuWandSparkles } from 'react-icons/lu';
import ConfirmationModal from './components/ConfirmationModal';
import CourseWizard from './components/course/CourseWizard';
import { courseDraftKey, readCourseDraft, isDraftResumable } from './components/course/courseDraft';
import BottomNav from './BottomNav';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import PageHeader from './components/PageHeader';
import { Permission } from './permissions';

const getCountryTileClass = (country) => {
  const c = country?.toLowerCase() || '';
  if (c.includes('spain')) return 'tile--spain';
  if (c.includes('united kingdom') || c.includes('uk') || c.includes('england') || c.includes('scotland') || c.includes('wales')) return 'tile--uk';
  if (c.includes('portugal')) return 'tile--portugal';
  return 'tile--default';
};

const SourceBadge = ({ source, isGlobal }) => {
  if (isGlobal) {
    if (source === 'llm_ingested') return <span className="source-badge source-badge--ai">AI</span>;
    return <span className="source-badge source-badge--global">Global</span>;
  }
  
  if (source === 'global_directory') {
    return <span className="source-badge source-badge--global">Global</span>;
  }
  
  return <span className="source-badge source-badge--local">Society</span>;
};

const CourseCard = memo(({ course, index, canManage, onEdit, onDelete, openSwipeId, setOpenSwipeId }) => {
  const cardRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, currentX: 0, isDragging: false, isScrolling: false });

  const hasActions = canManage;
  const actionsWidth = hasActions ? 140 : 0;
  const isOpen = openSwipeId === course.id;

  useEffect(() => {
    if (openSwipeId !== course.id && cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
      cardRef.current.style.transform = 'translateX(0)';
    }
  }, [openSwipeId, course.id]);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = { startX: touch.clientX, startY: touch.clientY, currentX: isOpen ? -actionsWidth : 0, isDragging: false, isScrolling: false };
    if (cardRef.current) cardRef.current.style.transition = 'none';
  }, [isOpen, actionsWidth]);

  const onTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchRef.current.startX;
    const deltaY = touch.clientY - touchRef.current.startY;
    if (!touchRef.current.isDragging && !touchRef.current.isScrolling) {
      if (Math.abs(deltaY) > Math.abs(deltaX)) { touchRef.current.isScrolling = true; return; }
      if (Math.abs(deltaX) > 10) touchRef.current.isDragging = true;
    }
    if (touchRef.current.isScrolling || !touchRef.current.isDragging) return;
    let translateX = isOpen ? deltaX - actionsWidth : deltaX;
    translateX = Math.min(0, Math.max(-actionsWidth, translateX));
    touchRef.current.currentX = translateX;
    if (cardRef.current) cardRef.current.style.transform = `translateX(${translateX}px)`;
  }, [isOpen, actionsWidth]);

  const onTouchEnd = useCallback(() => {
    if (cardRef.current) cardRef.current.style.transition = 'transform 0.3s ease';
    if (!touchRef.current.isDragging) return;
    const threshold = actionsWidth / 2;
    if (Math.abs(touchRef.current.currentX) > threshold) {
      if (cardRef.current) cardRef.current.style.transform = `translateX(-${actionsWidth}px)`;
      setOpenSwipeId(course.id);
    } else {
      if (cardRef.current) cardRef.current.style.transform = 'translateX(0)';
      if (isOpen) setOpenSwipeId(null);
    }
  }, [actionsWidth, isOpen, setOpenSwipeId, course.id]);

  const handleCardClick = useCallback(() => {
    if (touchRef.current.isDragging) return;
    if (isOpen) { setOpenSwipeId(null); return; }
    if (canManage) onEdit(course);
  }, [isOpen, setOpenSwipeId, canManage, onEdit, course]);

  return (
    <div className="swipe-container fade-in-up" style={{ animationDelay: `${index * 30}ms` }}>
      {hasActions && (
        <div className="swipe-actions" style={{ width: actionsWidth }}>
          <button className="swipe-action-btn swipe-edit" onClick={() => { setOpenSwipeId(null); onEdit(course); }}>
            <FiEdit size={20} />
            <span>Edit</span>
          </button>
          <button className="swipe-action-btn swipe-delete" onClick={() => { setOpenSwipeId(null); onDelete(course); }}>
            <FiTrash2 size={20} />
            <span>Delete</span>
          </button>
        </div>
      )}
      <div
        className="course-premium-card"
        ref={cardRef}
        onClick={handleCardClick}
        onTouchStart={hasActions ? onTouchStart : undefined}
        onTouchMove={hasActions ? onTouchMove : undefined}
        onTouchEnd={hasActions ? onTouchEnd : undefined}
      >
        <div className={`course-visual-strip ${course.source === 'global_directory' ? 'strip--personal' : 'strip--society'}`} />
        <div className={`course-visual-tile ${getCountryTileClass(course.country)}`}>
          <FiMapPin size={22} style={{ opacity: 0.9 }} />
        </div>
        <div className="course-card-info">
          <span className="course-card-name">{course.name}</span>
          <div className="course-card-meta-row">
            <SourceBadge source={course.source} isGlobal={false} />
            <span className="course-card-meta">
              {course.country ? `${course.country} · ` : ''}
              {course.tees?.length > 0
                ? `${course.tees.length} tee${course.tees.length !== 1 ? 's' : ''}`
                : course.course_rating != null ? 'Default tee' : 'No tees'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
});

const GlobalCourseCard = memo(({ course, index, canAdopt, adoptedIds, onAdopt, onEdit, openSwipeId, setOpenSwipeId }) => {
  const [adopting, setAdopting] = useState(false);
  const alreadyAdopted = adoptedIds.has(course.id);
  const cardRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, currentX: 0, isDragging: false, isScrolling: false });

  const hasEdit = !!onEdit;
  const actionsWidth = hasEdit ? 70 : 0;
  const isOpen = openSwipeId === course.id;

  useEffect(() => {
    if (openSwipeId !== course.id && cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
      cardRef.current.style.transform = 'translateX(0)';
    }
  }, [openSwipeId, course.id]);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = { startX: touch.clientX, startY: touch.clientY, currentX: isOpen ? -actionsWidth : 0, isDragging: false, isScrolling: false };
    if (cardRef.current) cardRef.current.style.transition = 'none';
  }, [isOpen, actionsWidth]);

  const onTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchRef.current.startX;
    const deltaY = touch.clientY - touchRef.current.startY;
    if (!touchRef.current.isDragging && !touchRef.current.isScrolling) {
      if (Math.abs(deltaY) > Math.abs(deltaX)) { touchRef.current.isScrolling = true; return; }
      if (Math.abs(deltaX) > 10) touchRef.current.isDragging = true;
    }
    if (touchRef.current.isScrolling || !touchRef.current.isDragging) return;
    let translateX = isOpen ? deltaX - actionsWidth : deltaX;
    translateX = Math.min(0, Math.max(-actionsWidth, translateX));
    touchRef.current.currentX = translateX;
    if (cardRef.current) cardRef.current.style.transform = `translateX(${translateX}px)`;
  }, [isOpen, actionsWidth]);

  const onTouchEnd = useCallback(() => {
    if (cardRef.current) cardRef.current.style.transition = 'transform 0.3s ease';
    if (!touchRef.current.isDragging) return;
    const threshold = actionsWidth / 2;
    if (Math.abs(touchRef.current.currentX) > threshold) {
      if (cardRef.current) cardRef.current.style.transform = `translateX(-${actionsWidth}px)`;
      setOpenSwipeId(course.id);
    } else {
      if (cardRef.current) cardRef.current.style.transform = 'translateX(0)';
      if (isOpen) setOpenSwipeId(null);
    }
  }, [actionsWidth, isOpen, setOpenSwipeId, course.id]);

  const handleAdopt = async (e) => {
    e.stopPropagation();
    if (alreadyAdopted || adopting) return;
    setAdopting(true);
    await onAdopt(course);
    setAdopting(false);
  };

  return (
    <div className="swipe-container fade-in-up" style={{ animationDelay: `${index * 30}ms` }}>
      {hasEdit && (
        <div className="swipe-actions" style={{ width: actionsWidth }}>
          <button className="swipe-action-btn swipe-edit" onClick={() => { setOpenSwipeId(null); onEdit(course); }}>
            <FiEdit size={20} />
            <span>Edit</span>
          </button>
        </div>
      )}
      <div
        className="course-premium-card course-premium-card--global"
        ref={cardRef}
        onTouchStart={hasEdit ? onTouchStart : undefined}
        onTouchMove={hasEdit ? onTouchMove : undefined}
        onTouchEnd={hasEdit ? onTouchEnd : undefined}
      >
        <div className="course-visual-strip strip--personal" />
        <div className={`course-visual-tile ${getCountryTileClass(course.country)}`}>
          <FiGlobe size={22} style={{ opacity: 0.9 }} />
        </div>
        <div className="course-card-info" style={{ flex: 1 }}>
          <div className="course-card-name-row">
            <span className="course-card-name">{course.name}</span>
          </div>
          <span className="course-card-meta">
            {course.country || 'Unknown location'}
            {course.tees?.length > 0 && ` · ${course.tees.length} tee${course.tees.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        {canAdopt && (
          <button
            className={`btn-adopt ${alreadyAdopted ? 'btn-adopt--done' : ''}`}
            onClick={handleAdopt}
            disabled={alreadyAdopted || adopting}
          >
            {alreadyAdopted ? <><FiBookmark size={14} /> Added</> : adopting ? '…' : <><FiPlus size={14} /> Add</>}
          </button>
        )}
      </div>
    </div>
  );
});

const REGIONS = ['global', 'UK', 'EU', 'US', 'AU'];

const STAGE_META = {
  discovery:           { icon: <FiSearch size={14} />,      label: 'Discovery',           color: '#3b82f6' },
  image_transcription: { icon: <FiImage size={14} />,       label: 'Image Transcription', color: '#8b5cf6' },
  extraction:          { icon: <FiCpu size={14} />,         label: 'Deep Extraction',     color: '#10b981' },
  complete:            { icon: <FiCheck size={14} />,       label: 'Complete',            color: '#10b981' },
};

function fmtTokens(n) {
  if (!n) return '0';
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}
function fmtMs(ms) {
  if (!ms) return '';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;
}

const CONFIDENCE_CHECKS = [
  { key: 'grounded_sources',        label: 'Grounded sources found' },
  { key: 'slopes_valid',            label: 'All slopes valid (55–155)' },
  { key: 'ratings_plausible',       label: 'Ratings plausible vs par' },
  { key: 'stroke_indices_complete', label: 'Stroke indices complete' },
];

function ConfidenceBar({ score, breakdown }) {
  const pct = Math.round((score || 0) * 100);
  const color = pct === 100 ? '#10b981' : pct >= 80 ? '#f59e0b' : '#ef4444';
  return (
    <div className="telem-confidence">
      <div className="telem-confidence-header">
        <span className="telem-confidence-label">Data Confidence</span>
        <span className="telem-confidence-score" style={{ color }}>{pct}%</span>
      </div>
      <div className="telem-confidence-bar-track">
        <div className="telem-confidence-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      {breakdown && (
        <div className="telem-confidence-checks">
          {CONFIDENCE_CHECKS.map(({ key, label }) => (
            <div key={key} className={`telem-check ${breakdown[key] ? 'telem-check--pass' : 'telem-check--fail'}`}>
              {breakdown[key] ? <FiCheck size={11} /> : <FiX size={11} />}
              <span>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StageCard({ stageKey, events, jobRunning }) {
  const [expanded, setExpanded] = useState(false);
  const meta = STAGE_META[stageKey] || STAGE_META.complete;

  const startEvent = events.find(e => e.status === 'started');
  const endEvent = events.find(e => e.status !== 'started');
  const isRunning = jobRunning && !!startEvent && !endEvent;
  const isSkipped = endEvent?.status === 'skipped';
  const isError = endEvent?.status === 'error' || endEvent?.status === 'not_found';
  const isFinal = stageKey === 'complete';

  const displayEvent = endEvent || startEvent;
  if (!displayEvent) return null;

  const tokensIn = endEvent?.tokens_in || 0;
  const tokensOut = endEvent?.tokens_out || 0;
  const tokensThinking = endEvent?.tokens_thinking || 0;
  const hasTokens = (tokensIn + tokensOut) > 0;
  const canExpand = !isSkipped && !isRunning && hasTokens;

  const iconColor = isError ? '#ef4444' : isSkipped ? '#475569' : meta.color;

  return (
    <div className={`telem-stage-card ${isRunning ? 'telem-stage--running' : ''} ${isSkipped ? 'telem-stage--skipped' : ''} ${isError ? 'telem-stage--error' : ''}`}>
      <div
        className={`telem-stage-header ${canExpand ? 'telem-stage-header--expandable' : ''}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="telem-stage-icon" style={{ color: iconColor }}>
          {isRunning
            ? <div className="telem-dot-spin" />
            : isSkipped ? <FiSkipForward size={14} />
            : isError ? <FiAlertCircle size={14} />
            : meta.icon}
        </div>
        <div className="telem-stage-info">
          <span className="telem-stage-name">{meta.label}</span>
          <span className="telem-stage-summary">{displayEvent.message}</span>
        </div>
        <div className="telem-stage-right">
          {endEvent?.duration_ms > 0 && (
            <span className="telem-stage-dur">{fmtMs(endEvent.duration_ms)}</span>
          )}
          {canExpand && (
            <FiChevronDown size={14} className={`telem-chevron ${expanded ? 'telem-chevron--open' : ''}`} />
          )}
        </div>
      </div>

      {expanded && hasTokens && (
        <div className="telem-stage-details">
          <div className="telem-detail-row">
            <span className="telem-detail-label">Model</span>
            <span className="telem-detail-value">{endEvent?.model || startEvent?.model}</span>
          </div>
          <div className="telem-detail-row">
            <span className="telem-detail-label">Tokens</span>
            <span className="telem-detail-value">
              {fmtTokens(tokensIn)} in · {fmtTokens(tokensOut)} out
              {tokensThinking > 0 && <span className="telem-thinking-badge"> +{fmtTokens(tokensThinking)} thinking</span>}
            </span>
          </div>
          {endEvent?.primary_source && (
            <div className="telem-detail-row">
              <span className="telem-detail-label">Source</span>
              <span className="telem-detail-value telem-detail-url">{endEvent.primary_source}</span>
            </div>
          )}
          {endEvent?.sources_found > 0 && (
            <div className="telem-detail-row">
              <span className="telem-detail-label">Sources</span>
              <span className="telem-detail-value">{endEvent.sources_found} found</span>
            </div>
          )}
        </div>
      )}

      {isFinal && endEvent?.status === 'success' && (
        <div className="telem-stage-final">
          {endEvent.confidence_score != null && (
            <ConfidenceBar score={endEvent.confidence_score} breakdown={endEvent.confidence_breakdown} />
          )}
          {endEvent.total_tokens_in != null && (
            <div className="telem-totals">
              Total: {fmtTokens((endEvent.total_tokens_in || 0) + (endEvent.total_tokens_out || 0))} tokens
              {endEvent.total_tokens_thinking > 0 && ` · ${fmtTokens(endEvent.total_tokens_thinking)} thinking`}
            </div>
          )}
          {endEvent.warnings?.length > 0 && (
            <div className="telem-warnings">
              {endEvent.warnings.map((w, i) => (
                <div key={i} className="telem-warning-item"><FiAlertCircle size={11} /> {w}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STAGE_ORDER = ['discovery', 'image_transcription', 'extraction', 'complete'];

function IngestionTelemetry({ telemetry, jobStatus }) {
  const running = jobStatus === 'running' || jobStatus === 'pending';
  const events = telemetry || [];

  // Group events by stage
  const grouped = {};
  for (const ev of events) {
    if (!grouped[ev.stage]) grouped[ev.stage] = [];
    grouped[ev.stage].push(ev);
  }

  return (
    <div className="telem-panel">
      <div className="telem-model-bar">
        <span className="telem-model-chip">1 · Search</span>
        <span className="telem-model-sep">›</span>
        <span className="telem-model-chip">1b · Image</span>
        <span className="telem-model-sep">›</span>
        <span className="telem-model-chip">2 · Deep Extract</span>
      </div>
      <div className="telem-stages">
        {events.length === 0 && (
          <div className="telem-stage-card telem-stage--running">
            <div className="telem-stage-header">
              <div className="telem-stage-icon" style={{ color: '#3b82f6' }}><div className="telem-dot-spin" /></div>
              <div className="telem-stage-info">
                <span className="telem-stage-name">Initialising</span>
                <span className="telem-stage-summary">Starting pipeline…</span>
              </div>
            </div>
          </div>
        )}
        {STAGE_ORDER.map(key => grouped[key] ? (
          <StageCard key={key} stageKey={key} events={grouped[key]} jobRunning={running} />
        ) : null)}
      </div>
    </div>
  );
}

function IngestionModal({ onClose, onSuccess, authenticatedFetch }) {
  const [step, setStep] = useState('search'); // search | select | confirm | loading | success | error
  const [courseName, setCourseName] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('global');
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searching, setSearching] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobResult, setJobResult] = useState(null);
  const [telemetry, setTelemetry] = useState([]);
  const [jobStatus, setJobStatus] = useState('pending');
  const [scorecardUrl, setScorecardUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [dupInfo, setDupInfo] = useState(null);       // existing directory record (if any) for the selected candidate
  const [refreshing, setRefreshing] = useState(false); // applying an update to a live (active) record
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const handleSearch = async () => {
    if (!courseName.trim()) return;
    setSearching(true);
    setCandidates([]);
    try {
      const res = await authenticatedFetch('/courses/search-candidates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_name: courseName.trim(), country: country.trim(), region }),
      });
      const data = await res.json();
      const results = data.candidates || [];
      setCandidates(results);
      if (results.length === 0) setErrorMsg('No matching courses found. Try a different name or country.');
      else { setErrorMsg(''); setStep('select'); }
    } catch {
      setErrorMsg('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleSelect = (candidate) => {
    setSelected(candidate);
    setDupInfo(null);
    setStep('confirm');
    // Warn the admin up-front if this course already exists in the directory.
    const c = resolveCountry(candidate);
    const params = new URLSearchParams({ name: candidate.name });
    if (c) params.set('country', c);
    authenticatedFetch(`/global-courses/lookup?${params}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.exists) setDupInfo(d); })
      .catch(() => {});
  };

  const resolveCountry = (selectedCandidate) => {
    // Prefer the validated dropdown selection
    if (country) return country;
    // Fall back: extract trailing part of location string (e.g. "Limassol, Cyprus" → "Cyprus")
    // and match against the known countries list for a clean value
    const raw = selectedCandidate.location?.split(',').pop()?.trim() || '';
    if (!raw) return '';
    const match = countries.find(c => c.toLowerCase() === raw.toLowerCase());
    return match || raw;
  };

  const handleImport = async () => {
    setStep('loading');
    try {
      const res = await authenticatedFetch('/courses/auto-create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          course_name: selected.name,
          country: resolveCountry(selected),
          location: selected.location || undefined,
          region,
          scorecard_url: scorecardUrl.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data.error || 'Failed to start ingestion.'); setStep('error'); return; }
      setJobId(data.job_id);
      pollRef.current = setInterval(() => pollJob(data.job_id), 2500);
    } catch {
      setErrorMsg('Request failed. Please try again.');
      setStep('error');
    }
  };

  const pollJob = async (id) => {
    try {
      const res = await authenticatedFetch(`/ingestion-jobs/${id}`);
      const data = await res.json();
      setTelemetry(data.telemetry || []);
      setJobStatus(data.status);
      if (data.status === 'success') {
        clearInterval(pollRef.current);
        setJobResult(data);
        setStep('success');
        onSuccess && onSuccess();
      } else if (data.status === 'failed') {
        clearInterval(pollRef.current);
        setErrorMsg(data.error_message || 'Ingestion failed.');
        setStep('error');
      }
    } catch { /* keep polling */ }
  };

  // Apply freshly-downloaded data to an already-live (active) directory record.
  const handleRefreshActive = async () => {
    if (!jobResult?.result_course_id || !jobId) return;
    setRefreshing(true);
    try {
      const res = await authenticatedFetch(
        `/global-courses/${jobResult.result_course_id}/apply-ingestion`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ job_id: jobId }) },
      );
      if (res.ok) { onSuccess && onSuccess(); onClose(); }
      else { const d = await res.json().catch(() => ({})); setErrorMsg(d.error || 'Could not refresh the directory record.'); setStep('error'); }
    } catch {
      setErrorMsg('Could not refresh the directory record.');
      setStep('error');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="ingestion-modal-overlay" onClick={onClose}>
      <div className="ingestion-modal" onClick={e => e.stopPropagation()}>
        <div className="ingestion-modal-header">
          <span className="ingestion-modal-title">
            <LuWandSparkles size={16} /> Auto-Create Course
          </span>
          <button className="ingestion-modal-close" onClick={onClose}><FiX size={20} /></button>
        </div>

        {/* SEARCH STEP */}
        {(step === 'search' || step === 'select') && (
          <div className="ingestion-modal-body">
            <div className="form-group">
              <label>Course Name</label>
              <input
                type="text"
                placeholder="e.g. Wentworth Club"
                value={courseName}
                onChange={e => setCourseName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                autoComplete="off"
                autoFocus
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Country</label>
                <select value={country} onChange={e => setCountry(e.target.value)}>
                  <option value="">Any</option>
                  {countries.map((c, i) => <option key={i} value={c} disabled={c.startsWith('-')}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Region</label>
                <select value={region} onChange={e => setRegion(e.target.value)}>
                  {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            {errorMsg && <p className="ingestion-error">{errorMsg}</p>}
            <button
              className="btn-sticky-save"
              style={{ width: '100%', marginTop: 4 }}
              onClick={handleSearch}
              disabled={searching || !courseName.trim()}
            >
              {searching ? 'Searching…' : <><FiSearch size={15} /> Search</>}
            </button>
          </div>
        )}

        {/* SELECT STEP */}
        {step === 'select' && candidates.length > 0 && (
          <div className="ingestion-candidates">
            <p className="ingestion-candidates-label">{candidates.length} result{candidates.length !== 1 ? 's' : ''} — select the correct course:</p>
            {candidates.map((c, i) => (
              <button key={i} className="ingestion-candidate-card" onClick={() => handleSelect(c)}>
                <div className="ingestion-candidate-info">
                  <span className="ingestion-candidate-name">{c.name}</span>
                  <span className="ingestion-candidate-meta">
                    <FiMapPin size={11} /> {c.location}
                    {c.type && <> · {c.type}</>}
                  </span>
                  {c.notes && <span className="ingestion-candidate-notes">{c.notes}</span>}
                </div>
                <FiPlus size={18} className="ingestion-candidate-add" />
              </button>
            ))}
          </div>
        )}

        {/* CONFIRM STEP */}
        {step === 'confirm' && selected && (
          <div className="ingestion-modal-body">
            <div className="ingestion-confirm-card">
              <span className="ingestion-candidate-name">{selected.name}</span>
              <span className="ingestion-candidate-meta"><FiMapPin size={11} /> {selected.location}</span>
              {selected.type && <span className="ingestion-candidate-notes">{selected.type}{selected.notes ? ` · ${selected.notes}` : ''}</span>}
            </div>
            {dupInfo && dupInfo.status === 'active' ? (
              <div className="ingestion-dup-notice ingestion-dup-notice--warn">
                <FiAlertCircle size={14} />
                <span>
                  <strong>{dupInfo.name}</strong> is already live in the directory. Continuing will
                  download fresh data and let you decide whether to refresh the live record.
                </span>
              </div>
            ) : dupInfo && dupInfo.status === 'rejected' ? (
              <div className="ingestion-dup-notice">
                <FiAlertCircle size={14} />
                <span>Previously rejected — it will be re-added to Pending Review for another look.</span>
              </div>
            ) : dupInfo && dupInfo.status === 'pending_review' ? (
              <div className="ingestion-dup-notice">
                <FiAlertCircle size={14} />
                <span>Already awaiting review — its data will be refreshed.</span>
              </div>
            ) : null}
            <p className="ingestion-confirm-text">Import the full scorecard for this course into the global directory?</p>
            <div className="form-group" style={{ marginTop: 8 }}>
              <label style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #94a3b8)' }}>
                Scorecard URL <span style={{ opacity: 0.6 }}>(optional — paste if AI struggles to find it)</span>
              </label>
              <input
                type="url"
                placeholder="https://www.clubwebsite.com/scorecard.pdf"
                value={scorecardUrl}
                onChange={e => setScorecardUrl(e.target.value)}
                style={{ fontSize: '0.78rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button className="btn-sticky-cancel" style={{ flex: 1 }} onClick={() => setStep('select')}>
                {dupInfo && dupInfo.status === 'active' ? 'Cancel' : 'Back'}
              </button>
              <button className="btn-sticky-save" style={{ flex: 2 }} onClick={handleImport}>
                <FiZap size={15} /> {dupInfo && dupInfo.status === 'active' ? 'Continue' : 'Import Scorecard'}
              </button>
            </div>
          </div>
        )}

        {/* LOADING STEP */}
        {step === 'loading' && (
          <div className="ingestion-modal-body" style={{ padding: '12px 16px 16px' }}>
            <div className="ingestion-loading-header">
              <span className="ingestion-loading-title">Importing {selected?.name}</span>
            </div>
            <IngestionTelemetry telemetry={telemetry} jobStatus={jobStatus} />
          </div>
        )}

        {/* SUCCESS STEP — active-record conflict: admin chooses whether to refresh */}
        {step === 'success' && jobResult?.result_course_status === 'active' && (
          <div className="ingestion-modal-body" style={{ padding: '12px 16px 16px' }}>
            <div className="ingestion-dup-notice ingestion-dup-notice--warn" style={{ marginBottom: 12 }}>
              <FiAlertCircle size={14} />
              <span>
                <strong>{selected?.name}</strong> is already live in the directory. Apply the freshly
                downloaded data to refresh that record? The live record stays active either way.
              </span>
            </div>
            <IngestionTelemetry telemetry={telemetry} jobStatus="success" />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button className="btn-sticky-cancel" style={{ flex: 1 }} onClick={onClose} disabled={refreshing}>
                Keep current
              </button>
              <button className="btn-sticky-save" style={{ flex: 2 }} onClick={handleRefreshActive} disabled={refreshing}>
                <FiRefreshCw size={15} /> {refreshing ? 'Refreshing…' : 'Refresh record'}
              </button>
            </div>
          </div>
        )}

        {/* SUCCESS STEP — saved to Pending Review */}
        {step === 'success' && jobResult?.result_course_status !== 'active' && (
          <div className="ingestion-modal-body" style={{ padding: '12px 16px 16px' }}>
            <div className="ingestion-success-header">
              <div className="ingestion-success-icon"><FiCheck size={20} /></div>
              <div>
                <p className="ingestion-success-title">Saved for Review</p>
                <p className="ingestion-success-name">{selected?.name}</p>
              </div>
            </div>
            <IngestionTelemetry telemetry={telemetry} jobStatus="success" />
            <button className="btn-sticky-save" style={{ width: '100%', marginTop: 12 }} onClick={onClose}>Done</button>
          </div>
        )}

        {/* ERROR STEP */}
        {step === 'error' && (
          <div className="ingestion-modal-body ingestion-error-state">
            <p className="ingestion-error-title">Import Failed</p>
            <p className="ingestion-error">{errorMsg}</p>
            <button className="btn-sticky-cancel" style={{ width: '100%', marginTop: 8 }} onClick={() => { setStep('search'); setErrorMsg(''); }}>Try Again</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CourseManager() {
  const { authenticatedFetch } = useApi();
  const { userProfile, hasPermission, currentSociety } = useAuth();

  const [activeTab, setActiveTab] = useState('my');
  const [globalCourses, setGlobalCourses] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [adoptedIds, setAdoptedIds] = useState(new Set());

  const [editingCourse, setEditingCourse] = useState(null);
  const [showCourseForm, setShowCourseForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  const [recentlyPlayedCourses, setRecentlyPlayedCourses] = useState([]);
  const [showIngestionModal, setShowIngestionModal] = useState(false);
  const [reviewingGlobalCourse, setReviewingGlobalCourse] = useState(null);
  const [pendingCourses, setPendingCourses] = useState([]);
  const [globalPage, setGlobalPage] = useState(1);
  const [globalPages, setGlobalPages] = useState(1);
  const [globalTotal, setGlobalTotal] = useState(0);
  const [selectedPending, setSelectedPending] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  const [openGlobalSwipeId, setOpenGlobalSwipeId] = useState(null);

  const isSystemAdmin = !!userProfile?.is_system_admin;
  const canManageCourses = isSystemAdmin || hasPermission(Permission.MANAGE_COURSES);

  const fetchGlobalCourses = useCallback(async (page = 1, search = '') => {
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (search) params.set('search', search);
      const res = await authenticatedFetch(`/global-courses?${params}`);
      if (res.ok) {
        const data = await res.json();
        setGlobalCourses(data.courses);
        setGlobalTotal(data.total);
        setGlobalPages(data.pages);
      }
    } catch (e) { console.error(e); }
  }, [authenticatedFetch]);

  const fetchPendingCourses = useCallback(async () => {
    if (!isSystemAdmin) return;
    try {
      const res = await authenticatedFetch('/global-courses?status=pending_review');
      if (res.ok) setPendingCourses(await res.json());
    } catch (e) { console.error(e); }
  }, [authenticatedFetch, isSystemAdmin]);

  const fetchMyCourses = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/courses');
      if (res.ok) {
        const data = await res.json();
        setMyCourses(data);
        setAdoptedIds(new Set(data.filter(c => c.global_course_id).map(c => c.global_course_id)));
      }
    } catch (e) { console.error(e); }
  }, [authenticatedFetch]);

  const fetchRecentlyPlayed = useCallback(async () => {
    try {
      const res = await authenticatedFetch('/courses/recently-played');
      if (res.ok) setRecentlyPlayedCourses(await res.json());
    } catch (e) { console.error(e); }
  }, [authenticatedFetch]);

  useEffect(() => {
    const delay = searchTerm ? 300 : 0;
    const timer = setTimeout(() => fetchGlobalCourses(globalPage, searchTerm), delay);
    return () => clearTimeout(timer);
  }, [globalPage, searchTerm, fetchGlobalCourses]);
  useEffect(() => { fetchMyCourses(); }, [fetchMyCourses]);
  useEffect(() => { fetchPendingCourses(); }, [fetchPendingCourses]);
  useEffect(() => { fetchRecentlyPlayed(); }, [fetchRecentlyPlayed]);

  const openReview = (course) => setReviewingGlobalCourse(course);

  const closeReview = () => setReviewingGlobalCourse(null);

  // The Global Directory is edited in the SAME wizard as a society's own
  // courses (components/course/CourseWizard.js) — only the endpoint and the
  // footer differ, so only those live here. Each returns the wizard's
  // { ok, saved?, error? } shape.
  const putGlobalCourse = (payload) =>
    authenticatedFetch(`/global-courses/${reviewingGlobalCourse.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

  const submitReview = async (payload) => {
    const res = await putGlobalCourse(payload);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { ok: false, error: err.error || 'Could not save this course.' };
    }
    const saved = await res.json().catch(() => null);
    await Promise.all([fetchGlobalCourses(), fetchPendingCourses()]);
    return { ok: true, saved };
  };

  // Approving sends the reviewer's corrections FIRST, so what is approved is
  // what they were looking at — and a failed save must not approve the old
  // data behind their back.
  const submitApproval = async (payload) => {
    const put = await putGlobalCourse(payload);
    if (!put.ok) {
      const err = await put.json().catch(() => ({}));
      return { ok: false, error: err.error || 'Could not save your corrections, so nothing was approved.' };
    }
    const res = await authenticatedFetch(`/global-courses/${reviewingGlobalCourse.id}/approve`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { ok: false, error: err.error || 'Your corrections were saved, but the course could not be approved.' };
    }
    await Promise.all([fetchGlobalCourses(), fetchPendingCourses()]);
    return { ok: true, saved: null };
  };

  const rejectReview = async () => {
    const res = await authenticatedFetch(`/global-courses/${reviewingGlobalCourse.id}/reject`, { method: 'POST' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      return { ok: false, error: err.error || 'Could not reject this course.' };
    }
    await fetchPendingCourses();
    closeReview();
    return { ok: true };
  };


  const handleBulkApprove = async () => {
    if (selectedPending.size === 0) return;
    setBulkApproving(true);
    const res = await authenticatedFetch('/global-courses/bulk-approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [...selectedPending] }),
    });
    if (res.ok) {
      setSelectedPending(new Set());
      await Promise.all([fetchGlobalCourses(), fetchPendingCourses()]);
    }
    setBulkApproving(false);
  };

  const handleBulkReject = async () => {
    if (selectedPending.size === 0) return;
    setBulkApproving(true);
    const res = await authenticatedFetch('/global-courses/bulk-reject', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: [...selectedPending] }),
    });
    if (res.ok) {
      setSelectedPending(new Set());
      await fetchPendingCourses();
    }
    setBulkApproving(false);
  };

  const handleAdopt = useCallback(async (globalCourse) => {
    const res = await authenticatedFetch(`/global-courses/${globalCourse.id}/adopt`, { method: 'POST' });
    if (res.ok) {
      await fetchMyCourses();
    } else {
      const data = await res.json().catch(() => ({}));
      if (res.status === 409) {
        await fetchMyCourses();
      } else {
        notify.error(data.error || 'Failed to add course.');
      }
    }
  }, [authenticatedFetch, fetchMyCourses]);

  const closeForm = () => {
    setEditingCourse(null);
    setShowCourseForm(false);
  };

  // A course card is typed up with the club's own website open alongside, so
  // the app being reloaded while the user is away is the NORMAL case, not an
  // edge one — and it drops them back on this list rather than in the wizard
  // they were halfway through. If a recent unsaved entry is sitting in storage,
  // reopen the wizard, which then restores it (and says so).
  const resumeChecked = useRef(false);
  useEffect(() => {
    if (resumeChecked.current || !currentSociety?.id || !canManageCourses) return;
    resumeChecked.current = true;
    if (isDraftResumable(readCourseDraft(courseDraftKey(currentSociety.id, null)))) {
      setEditingCourse(null);
      setShowCourseForm(true);
    }
  }, [currentSociety?.id, canManageCourses]);

  const handleEditClick = (c) => {
    setEditingCourse(c);
    setShowCourseForm(true);
  };

  // Called by CourseWizard after a successful create/update.
  const handleWizardSaved = async (savedCourse) => {
    if (savedCourse?.id) {
      setMyCourses(prev => prev.map(c => c.id === savedCourse.id ? savedCourse : c));
      setRecentlyPlayedCourses(prev => prev.map(c => c.id === savedCourse.id ? savedCourse : c));
    }
    await fetchMyCourses();
    closeForm();
  };

  const confirmDelete = async () => {
    if (!courseToDelete) return;
    const res = await authenticatedFetch(`/courses/${courseToDelete.id}`, { method: 'DELETE' });
    if (res.ok) fetchMyCourses();
    else if (res.status === 409) notify.error((await res.json()).error);
    setIsConfirmOpen(false);
    setCourseToDelete(null);
  };

  const filteredGlobal = globalCourses; // filtered server-side
  // Sort "All Courses" by country, biggest country group first (e.g. UK if it
  // has the most), then alphabetically by course name within each country.
  const filteredMy = (() => {
    const list = myCourses.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()));
    const counts = {};
    list.forEach(c => { const k = c.country || '￿'; counts[k] = (counts[k] || 0) + 1; });
    return list.slice().sort((a, b) => {
      const ca = a.country || '￿', cb = b.country || '￿';
      if (counts[cb] !== counts[ca]) return counts[cb] - counts[ca]; // larger group first
      if (ca !== cb) return ca.localeCompare(cb);                    // tie-break: country name
      return a.name.localeCompare(b.name);                           // within country: alphabetical
    });
  })();

  const menuItems = [
    { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
    { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
    { name: 'Play Golf', route: '/play', icon: <GiGolfTee size={24} /> },
    { name: 'Manage', route: '/setup', icon: <FiSettings size={24} />, active: true },
  ];

  return (
    <div className="course-manager-page">
      <ConfirmationModal
        tone="danger"
        isOpen={isConfirmOpen}
        title="Delete Course?"
        message={`Delete ${courseToDelete?.name}?`}
        onConfirm={confirmDelete}
        onCancel={() => setIsConfirmOpen(false)}
      />
      <PageHeader title="Courses" backTo="/setup" backLabel="Back to Manage" />

      <div className="course-manager-content-wrapper">
        {reviewingGlobalCourse ? (
          <CourseWizard
            /* Keyed so moving to another course under review is a fresh
               editor, not the last one's state. */
            key={`review-${reviewingGlobalCourse.id}`}
            mode="edit"
            course={reviewingGlobalCourse}
            countries={countries}
            authenticatedFetch={authenticatedFetch}
            draftScope="global"
            title={reviewingGlobalCourse.status === 'active' ? 'Edit Course' : 'Review Course'}
            headerExtra={
              <div className="review-wizard-meta">
                {reviewingGlobalCourse.status === 'pending_review' && (
                  <span className="review-pending-label">Pending approval</span>
                )}
                {reviewingGlobalCourse.confidence_score != null && (
                  <div className="review-confidence-wrapper">
                    <ConfidenceBar score={reviewingGlobalCourse.confidence_score} />
                  </div>
                )}
              </div>
            }
            /* Pending: the primary act is approval, which saves first. Active:
               a plain save. Either way the wizard's own validation runs before
               anything is written — which the old review form never did. */
            submit={reviewingGlobalCourse.status === 'pending_review' ? submitApproval : submitReview}
            saveLabel={reviewingGlobalCourse.status === 'pending_review' ? 'Approve' : 'Save'}
            savedLabel={reviewingGlobalCourse.status === 'pending_review' ? '✓ Approved' : '✓ Saved'}
            extraActions={reviewingGlobalCourse.status === 'pending_review' ? [{
              key: 'reject',
              label: 'Reject',
              className: 'cw-btn--danger',
              onClick: rejectReview,
              confirm: {
                title: 'Reject this course?',
                message: `${reviewingGlobalCourse.name} will be dropped from the directory rather than published.`,
                confirmText: 'Reject',
              },
            }] : []}
            onClose={closeReview}
            onSaved={closeReview}
          />
        ) : !showCourseForm ? (
          <>
            <div className="course-manager-controls">

            </div>

            <p className="info-note info-note--tight">
              <FiInfo size={15} />
              <span>
                <strong>My Courses</strong> is your own list — add to it from the
                <strong> Global Directory</strong>, or create a course yourself.
              </span>
            </p>

            {/* Tab Bar */}
            <div className="course-tab-bar">
              <button
                className={`course-tab ${activeTab === 'global' ? 'course-tab--active' : ''}`}
                onClick={() => setActiveTab('global')}
              >
                <FiGlobe size={15} />
                Global Directory
              </button>
              <button
                className={`course-tab ${activeTab === 'my' ? 'course-tab--active' : ''}`}
                onClick={() => setActiveTab('my')}
              >
                <FiBookmark size={15} />
                My Courses
                {myCourses.length > 0 && <span className="course-tab-count">{myCourses.length}</span>}
              </button>
            </div>

            <div className="course-search-container">
              <div className="search-box-modern">
                <FiSearch className="search-icon-modern" />
                <input
                  type="text"
                  className="search-input-modern"
                  placeholder={activeTab === 'global' ? 'Search global directory...' : 'Search my courses...'}
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setGlobalPage(1); }}
                />
              </div>
            </div>

            {activeTab === 'my' && canManageCourses && (
              <button
                className="btn-new"
                onClick={() => { setEditingCourse(null); setShowCourseForm(true); }}
              >
                <FiPlus size={18} /> New Course
              </button>
            )}

            {activeTab === 'global' && isSystemAdmin && (
              <button className="btn-new" onClick={() => setShowIngestionModal(true)}>
                <LuWandSparkles size={17} /> Import a Course
              </button>
            )}

            {activeTab === 'global' && (
              <div className="course-cards-list">
                {isSystemAdmin && pendingCourses.length > 0 && (
                  <div className="pending-section">
                    <div className="pending-section-header">
                      <div style={{ flex: 1 }} />
                      {selectedPending.size === 0 && (
                        <>
                          <span>Pending Review</span>
                          <span className="course-tab-count">{pendingCourses.length}</span>
                        </>
                      )}
                      {selectedPending.size > 0 && (
                        <>
                          <button className="btn-bulk-reject" onClick={handleBulkReject} disabled={bulkApproving}>
                            <FiX size={13} /> Reject {selectedPending.size}
                          </button>
                          <button className="btn-bulk-approve" onClick={handleBulkApprove} disabled={bulkApproving}>
                            <FiCheck size={13} /> Approve {selectedPending.size}
                          </button>
                        </>
                      )}
                      <label className="pending-select-all">
                        <input
                          type="checkbox"
                          checked={selectedPending.size === pendingCourses.length}
                          onChange={e => setSelectedPending(e.target.checked ? new Set(pendingCourses.map(c => c.id)) : new Set())}
                        />
                      </label>
                    </div>
                    {pendingCourses.map((c, i) => {
                      const pct = c.confidence_score != null ? Math.round(c.confidence_score * 100) : null;
                      const confColor = pct == null ? '#9ca3af' : pct === 100 ? '#10b981' : pct >= 80 ? '#f59e0b' : '#ef4444';
                      const isSelected = selectedPending.has(c.id);
                      return (
                        <div key={c.id} className={`course-premium-card course-premium-card--pending fade-in-up${isSelected ? ' course-premium-card--selected' : ''}`} style={{ animationDelay: `${i * 30}ms` }}>
                          <div className="course-visual-strip strip--personal" style={{ background: 'linear-gradient(to bottom, #10b981, #34d399)' }} />
                          <div className="course-visual-tile" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', width: 30, height: 30, borderRadius: 8, marginRight: 10 }}>
                            <LuWandSparkles size={13} />
                          </div>
                          <div className="course-card-info" style={{ flex: 1 }}>
                            <div className="course-card-name-row">
                              <span className="course-card-name">{c.name}</span>
                            </div>
                            <span className="course-card-meta">{c.country || 'Unknown'} · {c.tees?.length || 0} tees</span>
                          </div>
                          <button className="btn-review" onClick={() => openReview(c)}>
                            <span>Review</span>
                            {pct != null && <span className="btn-review-score" style={{ color: confColor }}>{pct}%</span>}
                          </button>
                          <label className="pending-card-checkbox" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={e => setSelectedPending(prev => {
                                const next = new Set(prev);
                                e.target.checked ? next.add(c.id) : next.delete(c.id);
                                return next;
                              })}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
                {filteredGlobal.map((c, i) => (
                  <GlobalCourseCard
                    key={c.id}
                    course={c}
                    index={i}
                    canAdopt={canManageCourses}
                    adoptedIds={adoptedIds}
                    onAdopt={handleAdopt}
                    onEdit={isSystemAdmin ? openReview : undefined}
                    openSwipeId={openGlobalSwipeId}
                    setOpenSwipeId={setOpenGlobalSwipeId}
                  />
                ))}
                {filteredGlobal.length === 0 && pendingCourses.length === 0 && (
                  <div className="course-empty-state">
                    <FiGlobe size={32} />
                    <p>{searchTerm ? 'No courses match your search.' : 'No courses in the global directory yet.'}</p>
                  </div>
                )}
                {globalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="page-btn"
                      disabled={globalPage === 1}
                      onClick={() => setGlobalPage(p => p - 1)}
                      aria-label="Previous page"
                    >
                      <FiChevronLeft size={16} />
                    </button>
                    <span className="page-indicator">{globalPage} of {globalPages} <span className="page-total">({globalTotal} courses)</span></span>
                    <button
                      className="page-btn"
                      disabled={globalPage === globalPages}
                      onClick={() => setGlobalPage(p => p + 1)}
                      aria-label="Next page"
                    >
                      <FiChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'my' && (
              <div className="course-cards-list">
                {searchTerm === '' && recentlyPlayedCourses.length > 0 && (
                  <div className="recently-played-section" style={{ marginBottom: '24px' }}>
                    <div className="section-header-compact">
                      <span className="section-title-premium">Recently Played</span>
                    </div>
                    {recentlyPlayedCourses.map((c, i) => (
                      <CourseCard
                        key={`recent-${c.id}`}
                        course={c}
                        index={i}
                        canManage={canManageCourses}
                        onEdit={handleEditClick}
                        onDelete={(course) => { setCourseToDelete(course); setIsConfirmOpen(true); }}
                        openSwipeId={openSwipeId}
                        setOpenSwipeId={setOpenSwipeId}
                      />
                    ))}
                    <div className="section-divider-premium" />
                    <div className="section-header-compact" style={{ marginTop: '16px' }}>
                      <span className="section-title-premium">All Courses</span>
                      <span className="course-tab-count">{filteredMy.length}</span>
                    </div>
                  </div>
                )}
                {filteredMy.map((c, i) => (
                  <CourseCard
                    key={c.id}
                    course={c}
                    index={searchTerm === '' ? i + 2 : i}
                    canManage={canManageCourses}
                    onEdit={handleEditClick}
                    onDelete={(course) => { setCourseToDelete(course); setIsConfirmOpen(true); }}
                    openSwipeId={openSwipeId}
                    setOpenSwipeId={setOpenSwipeId}
                  />
                ))}
                {filteredMy.length === 0 && (
                  <div className="course-empty-state">
                    <FiBookmark size={32} />
                    <p>{searchTerm ? 'No courses match your search.' : 'No courses yet. Add one from the Global Directory.'}</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <CourseWizard
            mode={editingCourse ? 'edit' : 'create'}
            course={editingCourse}
            countries={countries}
            authenticatedFetch={authenticatedFetch}
            onClose={closeForm}
            onSaved={handleWizardSaved}
          />
        )}
      </div>


      {showIngestionModal && (
        <IngestionModal
          onClose={() => setShowIngestionModal(false)}
          onSuccess={() => { fetchGlobalCourses(); fetchPendingCourses(); }}
          authenticatedFetch={authenticatedFetch}
        />
      )}

      <BottomNav menuItems={menuItems} />
    </div>
  );
}

export default CourseManager;
